import {
  ArrowDownToLine,
  ArrowUpFromLine,
  LineChart,
  Settings2,
  ShieldCheck,
  MessageSquareText,
  Activity,
  Sparkles,
  ListTodo,
  GitBranch,
  AlertTriangle,
  TrendingUp,
  FileBarChart,
  Wind,
  type LucideIcon,
} from "lucide-react";
import type { RoleId } from "./roles";

// The six navigation perspectives (ADR-014, amended by stories #833, #960; Kaksonen retired
// from nav, this story) — a content-lens axis orthogonal to role and scope. Nav rows are
// visible to every role except Konfiguraatio, which is hidden for non-`configuration` roles
// (see PerspectiveSidebar); role otherwise only picks the default (below) and tunes emphasis.
// `indoor-conditions` filters by the zone axis (source_type = zone), not by Finding.category —
// it owns no Finding category of its own. Kaksonen is no longer a
// perspective and has no frontend route, so it carries no `PerspectiveId`/`PerspectivePath`
// identity here.
export type PerspectiveId =
  | "savings-potential"
  | "energy-efficiency"
  | "indoor-conditions"
  | "equipment-performance"
  | "data-quality"
  | "configuration";

export type PerspectivePath =
  | "/savings-potential"
  | "/energy-efficiency"
  | "/indoor-conditions"
  | "/equipment-performance"
  | "/data-quality"
  | "/configuration";

export type Perspective = {
  id: PerspectiveId;
  to: PerspectivePath;
  label: string; // sidebar display label (terminology.md) — may differ from the canonical lens
  icon: LucideIcon;
  tagline: string;
};

export const PERSPECTIVES: Perspective[] = [
  {
    id: "savings-potential",
    to: "/savings-potential",
    label: "Säästöpotentiaali",
    icon: ArrowDownToLine,
    tagline: "Suunta ja rajat · säästöpotentiaali",
  },
  {
    id: "energy-efficiency",
    to: "/energy-efficiency",
    label: "Energiatehokkuus",
    icon: ArrowUpFromLine,
    tagline: "Reseptit ja havainnot · autonominen silmukka",
  },
  {
    id: "indoor-conditions",
    to: "/indoor-conditions",
    label: "Sisäilmaolosuhteet",
    icon: Wind,
    tagline: "Vyöhykkeiden olosuhteet · elävä tila ja havainnot",
  },
  {
    id: "equipment-performance",
    to: "/equipment-performance",
    label: "Talotekniikan toimivuus",
    icon: LineChart,
    tagline: "Toimiiko talotekniikka niin kuin pitäisi",
  },
  {
    id: "data-quality",
    to: "/data-quality",
    label: "Datan laatu",
    icon: ShieldCheck,
    tagline: "Signaalit ja havainnot · datan laatu ja luotettavuus",
  },
  {
    id: "configuration",
    to: "/configuration",
    label: "Konfiguraatio",
    icon: Settings2,
    tagline: "Rakenne · signaalit · odotettu toiminta",
  },
];

export function perspectiveIdFromPath(pathname: string): PerspectiveId | null {
  const match = PERSPECTIVES.find((p) => pathname.startsWith(p.to));
  return match ? match.id : null;
}

export function perspectivePath(id: PerspectiveId): PerspectivePath {
  return (PERSPECTIVES.find((p) => p.id === id) ?? PERSPECTIVES[0]).to;
}

// Content that once lived in a `buildings/$id` detail tab and has since moved out to a perspective
// or a Toiminto. Used to (a) redirect old `#optimointi` / `#simulointi` bookmarks and (b) resolve
// agent/dashboard cross-links to the owning surface. Simulointi (the "what if" säästöpotentiaali
// tool) is strategy-tier evidence, so it maps to Strategia. Trendi and Älyanalyysi/Kiinteistöraportti
// relocated to their own Toiminnot (`/trend`, `/reports` — neither is a perspective), so the map's
// value type widens minimally to admit them.
export const tabToPerspective: Record<
  string,
  PerspectivePath | "/trend" | "/reports"
> = {
  optimointi: "/energy-efficiency",
  simulointi: "/savings-potential",
  trendi: "/trend",
  alyraportti: "/reports",
  equipment: "/equipment-performance",
  zones: "/indoor-conditions",
  datapoints: "/data-quality",
  configuration: "/configuration",
};

// Role → default perspective (the home lens on login). Keyed by the current frontend
// RoleId; Phase 5 migrates these keys to the ADR-012 canonical set.
export const defaultPerspective: Record<RoleId, PerspectivePath> = {
  owner: "/savings-potential",
  operations: "/energy-efficiency",
  service: "/energy-efficiency",
  configuration: "/configuration",
  digital_team: "/data-quality",
};

// Secondary/action nav — the recommendations surface keeps its role-appropriate label.
export type ActionItem = {
  to:
    | "/chat"
    | "/recommendations"
    | "/findings"
    | "/activity"
    | "/trend"
    | "/reports";
  label: string;
  icon: LucideIcon;
};

// Havainnot — the open-findings feed, restored to the Toiminnot nav (story #833).
const findingsAction: ActionItem = {
  to: "/findings",
  label: "Havainnot",
  icon: AlertTriangle,
};

// Trendi — the historical/predictive signal explorer. A tool you open, not a judgment lens, so
// it is a Toiminto, never a perspective and never embedded in
// Analyysi.
const trendiAction: ActionItem = {
  to: "/trend",
  label: "Trendi",
  icon: TrendingUp,
};

// Kiinteistöraportti — the Älyanalyysi / Smart Report surface.
// Scope-aware like Trendi: building report at building scope, portfolio/salkku compare
// otherwise. A tool you open, not a judgment lens, so it is a Toiminto, not embedded in Analyysi.
const reportAction: ActionItem = {
  to: "/reports",
  label: "Kiinteistöraportti",
  icon: FileBarChart,
};

const recommendationsAction: Record<RoleId, ActionItem> = {
  owner: { to: "/recommendations", label: "Suositukset", icon: Sparkles },
  operations: { to: "/recommendations", label: "Työjono", icon: ListTodo },
  service: { to: "/recommendations", label: "Työjono", icon: ListTodo },
  configuration: {
    to: "/recommendations",
    label: "Suositukset",
    icon: Sparkles,
  },
  digital_team: {
    to: "/recommendations",
    label: "Mallit & säännöt",
    icon: GitBranch,
  },
};

export function actionNav(role: RoleId): ActionItem[] {
  // Honest general/portfolio assistant for every role — this is not
  // the building-wide Konfigurointiassistentti deep-assist, which stays the dedicated
  // on-demand Rakenne drawer, so it never carries a config-sounding label here.
  const converse: ActionItem = {
    to: "/chat",
    label: "Keskustele kiinteistöjen kanssa",
    icon: MessageSquareText,
  };
  const activity: ActionItem = {
    to: "/activity",
    label: "Tapahtumaloki",
    icon: Activity,
  };
  // Order: tools (Trendi, Kiinteistöraportti) → recommendations → Havainnot → Tapahtumaloki.
  return [
    converse,
    trendiAction,
    reportAction,
    recommendationsAction[role],
    findingsAction,
    activity,
  ];
}
